import { Box, Button, Flex, Image, Text } from "@chakra-ui/react";
import React, { memo, useEffect } from "react"
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styled from 'styled-components';
import { useRecoilState, useRecoilValue } from "recoil";
import { isLoadingState } from "../../../store/isLoadingState";
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorMessage } from "../../molecules/form/ErrorMessage";
import { CircularLoading } from "../../molecules/loading/CircularLoading";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Rating } from "@mui/material";
import { lineAccountIdState } from "../../../store/fetchedData/lineAccountIdState";
import { SELECTED_QUESTION } from "../../../types/suervyTypes";
import { apiUrlSoCouponSurveyAnswer, apiUrlSoCouponSurveySelectedQuestion } from "../../../apiUrls/soCouponApiUrls";
import { surveySchema } from "../../../yupSchema/surveySchema";
import { customNoAuthAxios } from "../../../utils/customNoAuthAxios";
import { useSwrNoAuth } from "../../../hooks/swr/useSwrNoAuth";

const SRating = styled(Rating)`
    font-size: 3rem!important;
    margin-top: 8px;
`;

// 必要な設定項目のみ型定義
type SoCouponSetting = {
    branch_score: number;
    g_url: string;
    default_url?: string | null;
};

// デバッグ用フラグ（ビルド時に置換されます）
const IS_DEBUG = process.env.REACT_APP_MANLABO_DEBUG === 'true';

// alert 出力用ヘルパ
const debugAlert = (title: string, payload?: unknown) => {
    if (!IS_DEBUG) return;
    try {
        const lines: string[] = [`[SoCouponSurvey DEBUG] ${title}`];
        if (payload !== undefined) {
            lines.push(typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2));
        }
        alert(lines.join('\n'));
    } catch {
        alert(`[SoCouponSurvey DEBUG] ${title}`);
    }
};

export const SoCouponSurvey: React.FC = memo(() => {
    const { restaurantId } = useParams();
    const [loading, setIsLoading] = useRecoilState(isLoadingState)
    const theme = createTheme();
    const lineAccountId = useRecoilValue(lineAccountIdState)
    const navigate = useNavigate();
    const location = useLocation();

    const { handleSubmit, register, formState: { errors }, control } = useForm({
        resolver: yupResolver(surveySchema),
    });

    // 事前に決めた遷移先（PostSoCouponOverallScore で設定）
    const predefinedNextUrl =
        (location.state as any)?.nextUrl ??
        (() => {
            try {
                return sessionStorage.getItem("socoupon:nextUrl") ?? undefined;
            } catch {
                return undefined;
            }
        })();

    // 設定取得（branch_score, g_url, default_url）
    const { data: setting, isLoading: isLoadingSetting } =
        useSwrNoAuth<SoCouponSetting>(`socoupon/restaurants/${restaurantId}/setting/customer/`);

    // 回答済みの総合スコア取得（404は未回答扱い）
    const overallScoreUrl = lineAccountId && restaurantId
        ? `socoupon/customers/${lineAccountId}/overall-score/?restaurant_id=${restaurantId}`
        : null;
    const {
        data: overallScore,
        isLoading: isLoadingOverallScore,
        error: overallScoreError,
    } = useSwrNoAuth<number | null>(overallScoreUrl);
    const answeredScore = overallScoreError?.response?.status === 404 ? null : overallScore;

    // アンケート設問の取得
    const { data: surveys, isLoading } =
        useSwrNoAuth<SELECTED_QUESTION[]>(`${apiUrlSoCouponSurveySelectedQuestion}?restaurant_id=${restaurantId}`);

    // answeredScore が number のときだけ比較に使う（null → 0 の罠を回避）
    const scoreNum = typeof answeredScore === 'number' && Number.isFinite(answeredScore)
        ? answeredScore
        : undefined;

    // 「以上」判定
    const goGood = typeof scoreNum === 'number'
        ? scoreNum >= (setting?.branch_score ?? 0)
        : undefined;

    // 初期デバッグ表示（データ揃ったタイミング） - useEffectをここに移動
    useEffect(() => {
        if (!IS_DEBUG || !setting || !surveys) return;
        debugAlert("初期値ロード", {
            restaurantId,
            lineAccountId,
            predefinedNextUrl,
            overallScoreUrl,
            overallScore,
            answeredScore,
            scoreNum,
            goGood,
            setting: {
                branch_score: setting.branch_score,
                g_url: setting.g_url,
                default_url: setting.default_url ?? null,
            },
            surveys_count: surveys?.length ?? 0,
        });
    }, [restaurantId, lineAccountId, predefinedNextUrl, answeredScore, scoreNum, goGood, setting, surveys]);

    // スコアが閾値以上なら（＝良い評価なら）追加アンケートをスキップして g_url へ
    useEffect(() => {
        if (goGood !== true || !setting) return;

        // 使い終わった nextUrl をクリア
        try {
            sessionStorage.removeItem("socoupon:nextUrl");
        } catch { /* noop */ }

        const target = setting.g_url;

        // デバッグ: 早期リダイレクトの決定状況
        debugAlert("早期リダイレクト(good>=threshold)", {
            overallScoreUrl,
            overallScore,
            answeredScore,
            scoreNum,
            branch_score: setting.branch_score,
            goGood,
            g_url: setting.g_url,
            default_url: setting.default_url ?? null,
            finalTarget: target,
            finalTargetType: (() => {
                try {
                    const u = new URL(target);
                    return (u.protocol === 'http:' || u.protocol === 'https:') ? 'http(s)' : 'unknown';
                } catch {
                    return typeof target === 'string' && target.startsWith('/') ? 'internal' : 'invalid';
                }
            })(),
        });

        try {
            const u = new URL(target);
            if (u.protocol === 'http:' || u.protocol === 'https:') {
                window.location.href = target;
                return;
            }
        } catch { /* not an absolute URL */ }

        if (typeof target === 'string' && target.startsWith('/')) {
            navigate(target, { replace: true });
        } else {
            navigate('/thanks', { replace: true });
        }
    }, [goGood, setting, navigate]);

    // デバッグ: 追加アンケートを表示するケース（goGood !== true）
    useEffect(() => {
        if (!IS_DEBUG || !setting) return;
        if (goGood !== true) {
            debugAlert("追加アンケートを表示", {
                overallScoreUrl,
                overallScore,
                answeredScore,
                scoreNum,
                branch_score: setting.branch_score,
                goGood,
                surveys_count: surveys?.length ?? 0,
                predefinedNextUrl,
                g_url: setting.g_url,
                default_url: setting.default_url ?? null,
            });
        }
    }, [goGood, setting, surveys, predefinedNextUrl]);

    // 早期リターンは全てのHooksの後に配置
    if (isLoading || isLoadingSetting || isLoadingOverallScore || !surveys || !setting) {
        return <CircularLoading />
    }

    const isHttpUrl = (u?: string | null) => {
        if (!u) return false;
        try {
            const url = new URL(u);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
            return false;
        }
    };

    const isInternalPath = (u?: string | null) => !!u && u.startsWith('/');

    const onSubmit = async (data:any) => {
        setIsLoading(true)
        const questions = surveys.map(survey => survey.question);
        data['restaurant_id'] = restaurantId
        data['line_account_id'] = lineAccountId
        data['questions'] = questions

        try {
            await customNoAuthAxios.post(
                `${apiUrlSoCouponSurveyAnswer}`,
                data
            );

            // 回答後はスコアで分岐（scoreNum が無効なら default_url のフォールバック。未設定なら /thanks）
            const computedTarget =
                goGood === true ? setting.g_url : (setting.default_url || '/thanks');

            const target = computedTarget;

            // 遷移前デバッグ
            debugAlert("遷移先決定", {
                predefinedNextUrl,
                answeredScore,
                scoreNum,
                branch_score: setting.branch_score,
                goGood,
                g_url: setting.g_url,
                default_url: setting.default_url ?? null,
                computedTarget,
                finalTarget: target,
                finalTargetType: isHttpUrl(target) ? "http(s)" : (isInternalPath(target) ? "internal" : "invalid"),
            });

            // 使い終わった nextUrl をクリア
            try {
                sessionStorage.removeItem("socoupon:nextUrl");
            } catch { /* noop */ }

            // 遷移
            if (isHttpUrl(target)) {
                window.location.href = target!;
            } else if (isInternalPath(target)) {
                navigate(target!, { replace: true });
            } else {
                if (isHttpUrl(setting.g_url)) {
                    window.location.href = setting.g_url;
                } else {
                    navigate('/thanks', { replace: true });
                }
            }
        } catch (error: any) {
            debugAlert("回答送信エラー", {
                message: error?.message,
                status: error?.response?.status,
                data: error?.response?.data,
            });
            navigate('/thanks', { replace: true });
        } finally {
            setIsLoading(false)
        }
    };

    return (
        <Box py='50px'>
            <Flex justifyContent='center'>
                <Image w='200px' borderRadius='10px' src="/images/logo.png" />
            </Flex>
            <Box px="3">
                <br />
                <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
                    {surveys.map((survey, index) => (
                        <Box key={survey.id} mb='5'>
                            <Text as='b' fontSize='sm'>{survey.question_content}</Text>
                            <Box textAlign="center">
                                <Controller
                                    name={`survey.${index}.answer_score`}
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <ThemeProvider theme={theme}>
                                            <SRating
                                                name="score"
                                                size="large"
                                                onChange={(event, newValue) => {
                                                    field.onChange(newValue);
                                                }}
                                            />
                                        </ThemeProvider>
                                    )}
                                />
                            </Box>
                            <Box textAlign="center">
                                <ErrorMessage errorMessage={errors.survey?.[index]?.answer_score?.message ?? ''} />
                            </Box>
                        </Box>
                    ))}
                    <div>
                        <Text as='b' fontSize='sm' >自由解答欄</Text>
                        <textarea rows={5} className='form-control' {...register('answer_text')} />
                        {errors.answer_text?.message && <ErrorMessage errorMessage={errors.answer_text.message} />}
                    </div>
                    <br />
                    <Box textAlign="center">
                        <Button colorScheme="blue" w='75%' size='lg' type='submit' isDisabled={loading}>回答する</Button>
                    </Box>
                </form>
            </Box>
        </Box>
    );
});